import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Copy, MessageSquare, Mail, FileImage, Printer } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { referralUrl, longReferralUrl } from "../../lib/api";

const HEADLINES = ["Are You Behind on Your Mortgage?", "Facing Foreclosure?", "Behind on Property Taxes?", "Need to Sell Your Home Quickly?"];

const loadImg = (src) => new Promise((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = bad; i.src = src; });

async function brandedFlyer(qrPng) {
  const [front, qr] = await Promise.all([loadImg("/brand/flyer-front.jpg"), loadImg(qrPng)]);
  const c = document.createElement("canvas");
  c.width = front.width; c.height = front.height;
  const g = c.getContext("2d");
  g.drawImage(front, 0, 0);
  g.fillStyle = "#fff";
  g.fillRect(526, 338, 264, 245);
  g.drawImage(qr, 538, 340, 240, 240);
  return c.toDataURL("image/jpeg", 0.95);
}

const flyerHtml = (branded, url, ids, qr, headline) => branded ? `<!doctype html><html><head><title>AskJoe-AI Flyer</title><style>@page{margin:0}body{margin:0;text-align:center;font-family:Arial,sans-serif}
img{width:100%;max-width:820px;display:block;margin:0 auto}.ids{font:11px monospace;color:#64748B;padding:6px}.pg{page-break-after:always}</style></head><body>
<div class="pg"><img src="${branded}"/><div class="ids">${url} • ${ids}</div></div><img src="${window.location.origin}/brand/flyer-back.jpg"/>
<script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>` : legacyFlyer(qr, url, headline, ids);

const legacyFlyer = (img, url, headline, ids) => `<!doctype html><html><head><title>AskJoe-AI Flyer</title><style>
body{font-family:Arial,sans-serif;margin:0;padding:48px;color:#1E293B;text-align:center}h1{font-size:44px;margin:0 0 12px}
.brand{letter-spacing:.2em;font-size:13px;color:#B45309;font-weight:bold}.scan{font-size:24px;font-weight:800;margin:28px 0 12px}
.list{font-size:20px;line-height:1.7;margin:16px 0}.ids{font-family:monospace;font-size:11px;color:#64748B;margin-top:24px}
.disc{font-size:10px;color:#64748B;max-width:560px;margin:24px auto 0}</style></head><body>
<img src="${window.location.origin}/brand/logo.webp" width="360"/><h1>${headline}</h1>
<div class="list">${HEADLINES.filter((h) => h !== headline).join("<br/>")}</div>
<div class="scan">SCAN HERE TO UNDERSTAND YOUR OPTIONS.</div><img src="${img}" width="260" height="260"/>
<div style="font-size:16px;margin-top:8px">${url}</div><div class="ids">${ids}</div>
<div class="disc">AskJoe-AI provides educational information, document organization and referrals. AskJoe-AI is not a law firm. Scanning this code does not sign you up for marketing calls or texts.</div>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;

export const QrAsset = ({ link, campaign, compact }) => {
  const ref = useRef(null);
  const printRef = useRef(null);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [headline, setHeadline] = useState(HEADLINES[0]);
  const url = referralUrl(link.code);
  const qrValue = `${url}?src=qr`;
  const ids = [campaign?.id && `Campaign ${campaign.id}`, `QR ${link.id}`, link.asset_id && `Asset ${link.asset_id}`, link.partner_id && `Partner ${link.partner_id}`].filter(Boolean).join(" • ");
  const png = () => ref.current?.querySelector("canvas")?.toDataURL("image/png");
  const msg = `Hi${name ? ` ${name}` : ""}. Here is the secure AskJoe-AI link we discussed. Joe will walk you through some questions about your property and help organize your situation before one of our specialists reviews it: ${url}`;

  const download = () => { const a = document.createElement("a"); a.href = png(); a.download = `${link.id}-${link.code}.png`; a.click(); };
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  const sms = () => { window.location.href = `sms:${contact.includes("@") ? "" : contact}?&body=${encodeURIComponent(msg)}`; };
  const email = () => { window.location.href = `mailto:${contact.includes("@") ? contact : ""}?subject=${encodeURIComponent("Your secure AskJoe-AI link")}&body=${encodeURIComponent(msg)}`; };
  const flyer = async () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to open the flyer.");
    try { w.document.write(flyerHtml(await brandedFlyer(printRef.current.querySelector("canvas").toDataURL("image/png")), url, ids)); } catch { w.document.write(flyerHtml(null, url, ids, png(), headline)); }
    w.document.close();
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`qr-asset-${link.code}`}>
      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-[auto_1fr]"}`}>
        <div ref={printRef} className="hidden"><QRCodeCanvas value={qrValue} size={480} level="M" includeMargin /></div>
        <div ref={ref} className="w-fit rounded-md bg-white p-3"><QRCodeCanvas value={qrValue} size={compact ? 140 : 168} level="M" includeMargin={false} /></div>
        <div className="min-w-0 space-y-2 text-sm">
          <p className="font-semibold">{link.label}</p>
          <p className="font-mono text-xs text-muted-foreground">{ids}</p>
          <p className="break-all font-mono text-xs"><span className="text-muted-foreground">Short URL: </span><span data-testid={`short-url-${link.code}`}>{url}</span></p>
          <p className="break-all font-mono text-xs"><span className="text-muted-foreground">Referral URL: </span>{longReferralUrl(link.code)}</p>
          <div className="grid gap-2 pt-1 sm:grid-cols-2">
            <Input data-testid={`send-name-${link.code}`} placeholder="Recipient first name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
            <Input data-testid={`send-contact-${link.code}`} placeholder="Phone or email (optional)" value={contact} onChange={(e) => setContact(e.target.value)} className="h-9" />
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {[["Download QR", Download, download, "download"], ["Copy link", Copy, copy, "copy"], ["Send by text", MessageSquare, sms, "sms"], ["Send by email", Mail, email, "email"], ["Add to flyer", FileImage, flyer, "flyer"], ["Print campaign", Printer, flyer, "print"]].map(([l, Icon, fn, k]) => (
          <button key={k} data-testid={`qr-${k}-${link.code}`} onClick={fn} className="inline-flex items-center gap-1.5 rounded border border-border px-2.5 py-1.5 text-xs font-semibold transition-colors hover:border-primary hover:text-primary">
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
        <select data-testid={`flyer-headline-${link.code}`} title="Used only if the branded flyer image can't load" value={headline} onChange={(e) => setHeadline(e.target.value)} className="hidden">
          {HEADLINES.map((h) => <option key={h}>{h}</option>)}
        </select>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Text/email open your device's messaging app with the message prefilled. Direct sending from AskJoe is not connected yet.</p>
    </div>
  );
};
